import { createContext, useContext, useState } from 'react';
import { Timeline } from 'rsuite'
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { speakers } from '../../static/content';
import 'rsuite/Timeline/styles/index.css';
import 'rsuite/dist/rsuite.min.css';

const blackDot = <div style={{width:12,height:12,borderRadius:'50%',background:'#000',marginTop:4}} />;
type ActiveVideo = { name: string; title: string; url: string };
const VideoModalContext = createContext<((video: ActiveVideo) => void) | null>(null);

function SpeakerRow({ name, title }: { name: string, title: string }) {
    const setActiveVideo = useContext(VideoModalContext);
    const video = (speakers.find((speaker) => speaker.name === name) as any)?.video;
    const titleContent = video && setActiveVideo ? (
        <button
            type="button"
            className="inline p-0 border-0 bg-transparent text-left text-[#039876] underline underline-offset-2 hover:text-[#026b54] cursor-pointer"
            style={{ font: 'inherit' }}
            onClick={() => setActiveVideo({ name, title, url: video })}
        >
            <em>{title}</em>
        </button>
    ) : (
        <em>{title}</em>
    );

    return (
        <div className="flex items-start gap-2 mt-2">
            <div style={{width:8,height:8,borderRadius:'50%',background:'#9ca3af',marginTop:5,flexShrink:0}} />
            <p className="text-base text-gray-500">{name} — {titleContent}</p>
        </div>
    );
}

export default function Itinerary(): JSX.Element {
    const [activeVideo, setActiveVideo] = useState<ActiveVideo | null>(null);

    return (
        <VideoModalContext.Provider value={setActiveVideo}>
        <div style={{ background: '#039876' }} className="" id="itinerary">
            <div className='mx-6 md:mx-12 bg-white rounded-lg p-6'>
                <h1 className='text-4xl font-bold text-center select-none'>Itinerary</h1>
                <div className="grid md:grid-cols-2 mt-8">

                    {/* Sunday 6/14 */}
                    <div className='mb-6'>
                        <h1 className='text-center text-2xl font-bold mb-6 select-none'>Sunday 6/14</h1>
                        <Timeline className='mb-6'>
                            <Timeline.Item time="9:00am - 9:30am" dot={blackDot}>
                                <p className="text-lg font-bold">Welcome &amp; Introductions</p>
                            </Timeline.Item>
                            <Timeline.Item time="9:30am - 10:30am" dot={blackDot}>
                                <p className="text-lg font-bold">Presentations</p>
                                <SpeakerRow name="Yao-Yi Chiang" title="Spatial Contextualization for Autonomous Spatial Analysis" />
                                <SpeakerRow name="Zhenlong Li" title="Toward Trustworthy, Interoperable, and Scalable Autonomous GIS: Challenges and Solutions for Autonomous Spatial Analysis" />
                            </Timeline.Item>
                            <Timeline.Item time="10:30am - 11:00am" dot={blackDot}>
                                <p className="text-lg font-bold">Coffee Break</p>
                            </Timeline.Item>
                            <Timeline.Item time="11:00am - 12:00pm" dot={blackDot}>
                                <p className="text-lg font-bold">Lightning Talks</p>
                                <SpeakerRow name="Huan Ning" title="Agent-Hive: A Fractal Research Graph Architecture for Long-Running Scientific Agent Swarms" />
                                <SpeakerRow name="Jason Gilman" title="Towards an Agentic Queryable Earth" />
                                <SpeakerRow name="Gengchen Mai" title="Geo-knowledge Guided LLM Agent Frameworks for Complex Geospatial Question Answering" />
                                <SpeakerRow name="Wei Kang" title="Open-Source Spatial Analysis as Protocol-Ready Infrastructure for Autonomous Spatial Analysis" />
                                <SpeakerRow name="Peter Kedron" title="Accepting and Addressing Nonstandard Errors when Developing Autonomous Spatial Analysis" />
                            </Timeline.Item>
                            <Timeline.Item time="12:00pm - 12:30pm" dot={blackDot}>
                                <p className="text-lg font-bold">Discussion</p>
                            </Timeline.Item>
                            <Timeline.Item time="12:30pm - 1:30pm" dot={blackDot}>
                                <p className="text-lg font-bold">Lunch Break</p>
                            </Timeline.Item>
                            <Timeline.Item time="1:30pm - 2:40pm" dot={blackDot}>
                                <p className="text-lg font-bold">Lightning Talks</p>
                                <SpeakerRow name="Meiliu Wu" title="Teaching GeoAI to Know Its SPLACE" />
                                <SpeakerRow name="Ziqi Li" title="Can Tabular Foundation Models Effectively Model Spatial Data?" />
                                <SpeakerRow name="Vanessa Frias-Martinez" title="Do LLMs Know Your Neighborhood? Auditing LLM Priors for Neighborhood-Level Mobility Prediction and Structural Alignment" />
                                <SpeakerRow name="Di Zhu" title="Discovering governing spatial interaction mechanisms in dynamic spatiotemporal urban systems" />
                                <SpeakerRow name="Daniel Adams" title="A rethinking of when and how GeoAI should learn complex targets" />
                                <SpeakerRow name="Yue Lin" title="The Homogenizing Effects: GeoAI and the Loss of Geospatial Diversity" />
                            </Timeline.Item>
                            <Timeline.Item time="2:40pm - 3:10pm" dot={blackDot}>
                                <p className="text-lg font-bold">Discussion</p>
                            </Timeline.Item>
                            <Timeline.Item time="3:10pm - 3:40pm" dot={blackDot}>
                                <p className="text-lg font-bold">Coffee Break</p>
                            </Timeline.Item>
                            <Timeline.Item time="3:40pm - 4:40pm" dot={blackDot}>
                                <p className="text-lg font-bold">Presentations</p>
                                <SpeakerRow name="Arbaaz Muslim" title="Population Dynamics Foundation Model" />
                                <SpeakerRow name="Ghermay Araya" title="Designing GeoAI for Decisions That Matter: A Practitioner Case for the Domain Expert in the Middle" />
                            </Timeline.Item>
                            <Timeline.Item time="4:40pm - 5:00pm" dot={blackDot}>
                                <p className="text-lg font-bold">Wrap Up</p>
                            </Timeline.Item>
                            <Timeline.Item time="6:00pm - 7:00pm" dot={blackDot}>
                                <p className="text-lg font-bold">Happy Hour @ Grillmarx <span className="font-normal text-gray-500 text-base">(sponsored by NLT)</span></p>
                            </Timeline.Item>
                            <Timeline.Item time="7:00pm" dot={blackDot}>
                                <p className="text-lg font-bold">Dinner @ Grillmarx</p>
                            </Timeline.Item>
                            <Timeline.Item />
                        </Timeline>
                    </div>

                    {/* Monday 6/15 */}
                    <div className='mb-6'>
                        <h1 className='text-center text-2xl font-bold mb-6 select-none'>Monday 6/15</h1>
                        <Timeline className='mb-6'>
                            <Timeline.Item time="9:00am - 9:15am" dot={blackDot}>
                                <p className="text-lg font-bold">Summary Day I</p>
                            </Timeline.Item>
                            <Timeline.Item time="9:15am - 10:25am" dot={blackDot}>
                                <p className="text-lg font-bold">Lightning Talks</p>
                                <SpeakerRow name="Songhua Hu" title="From Survey to Simulation: An LLM-Centered Pipeline for Travel Behavior Modeling" />
                                <SpeakerRow name="Clinton Stipek" title="Orchestrating Spatial Data: Enhancing Geospatial Workflows via Hierarchical Agentic Systems" />
                                <SpeakerRow name="Devika Jain" title="Enhancing Spatial Research Metadata with Generative AI" />
                                <SpeakerRow name="Caglar Koylu" title="Uncertainty, Interpretation, and Domain Expertise in Autonomous Spatial Analysis" />
                                <SpeakerRow name="Mehak Sachdeva" title="The Step That Was Skipped: Scale and the Limits of Autonomous Spatial Analysis" />
                                <SpeakerRow name="Debs Ghosh" title="Can We Go Basic? Rethinking Spatial Reasoning, Data Equity, and Lived Experience in GeoAI" />
                            </Timeline.Item>
                            <Timeline.Item time="10:25am - 10:50am" dot={blackDot}>
                                <p className="text-lg font-bold">Discussion</p>
                            </Timeline.Item>
                            <Timeline.Item time="10:50am - 11:15am" dot={blackDot}>
                                <p className="text-lg font-bold">Coffee Break</p>
                            </Timeline.Item>
                            <Timeline.Item time="11:15am - 12:15pm" dot={blackDot}>
                                <p className="text-lg font-bold">Lightning Talks</p>
                                <SpeakerRow name="Wataru Morioka" title="Integrating GeoAI with Geographic Traditions: Hybrid Approaches for Advancing Spatial Inquiry" />
                                <SpeakerRow name="Jessica Breen" title="Towards a Situated GeoAI" />
                                <SpeakerRow name="Luyu Liu" title="Three Paradoxes of GeoAI" />
                                <SpeakerRow name="Gia Barboza-Salerno" title="The Spatial Scale of Justice: Toward Spatiolegal Reasoning in Autonomous GeoAI" />
                                <SpeakerRow name="Hongyu Zhang" title="Challenges of Integrating Generative AI into GIScience Education" />
                            </Timeline.Item>
                            <Timeline.Item time="12:15pm - 12:40pm" dot={blackDot}>
                                <p className="text-lg font-bold">Discussion</p>
                            </Timeline.Item>
                            <Timeline.Item time="12:40pm - 1:40pm" dot={blackDot}>
                                <p className="text-lg font-bold">Lunch Break</p>
                            </Timeline.Item>
                            <Timeline.Item time="1:40pm - 2:40pm" dot={blackDot}>
                                <p className="text-lg font-bold">Presentations</p>
                                <SpeakerRow name="John Wilson" title="What will geospatial programs look like in 2035 and how soon can we build credible GeoAI classes for inclusion in these programs?" />
                                <SpeakerRow name="Shaowen Wang" title="I-GUIDE AI: From Principles to Practice" />
                            </Timeline.Item>
                            <Timeline.Item time="2:40pm - 3:30pm" dot={blackDot}>
                                <p className="text-lg font-bold">Breakout Discussions</p>
                            </Timeline.Item>
                            <Timeline.Item time="3:30pm - 4:00pm" dot={blackDot}>
                                <p className="text-lg font-bold">Report Back &amp; Wrap Up</p>
                            </Timeline.Item>
                            <Timeline.Item />
                        </Timeline>
                    </div>

                </div>
            </div>
        </div>
        {activeVideo ? (
            <div
                className="fixed inset-0 z-[10000] bg-black/80 flex items-center justify-center p-4"
                role="dialog"
                aria-modal="true"
                aria-label={`${activeVideo.name} session video`}
                onClick={() => setActiveVideo(null)}
            >
                <div className="w-full max-w-5xl bg-black rounded-lg overflow-hidden shadow-2xl" onClick={(event) => event.stopPropagation()}>
                    <div className="flex items-center justify-between bg-white px-4 py-3">
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">{activeVideo.name}</h2>
                            <p className="text-sm text-gray-500"><em>{activeVideo.title}</em></p>
                        </div>
                        <button
                            className="h-8 w-8 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 flex items-center justify-center"
                            aria-label="Close video"
                            onClick={() => setActiveVideo(null)}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    </div>
                    <div className="aspect-video bg-black">
                        <iframe
                            title={`${activeVideo.name} session video`}
                            src={activeVideo.url}
                            className="h-full w-full"
                            allow="autoplay; fullscreen"
                            allowFullScreen
                        />
                    </div>
                </div>
            </div>
        ) : null}
        </VideoModalContext.Provider>
    )
}
